import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt,
  faGraduationCap,
  faUsers,
  faBook,
  faHeart,
  faStar,
  faCheckCircle,
  faRocket,
  faQuoteRight,
} from "@fortawesome/free-solid-svg-icons";
import ApiServices from "../Services/ApiServices";
import Loader from "../Shared/Loader";
import Header from "../Shared/Header";
import Footer from "../Shared/Footer";
import ownerImg from "../../assets/images/owner.jpg";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.65, delay: i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const ValueCard = ({ icon, title, desc, index }) => (
  <motion.div
    className="ab-value-card"
    variants={fadeUp}
    custom={index}
    whileHover={{ y: -5 }}
  >
    <div className="ab-value-icon-wrap">
      <FontAwesomeIcon icon={icon} />
    </div>
    <h3 className="ab-value-title">{title}</h3>
    <p className="ab-value-desc">{desc}</p>
  </motion.div>
);

const AboutUs = () => {
  const [aboutData, setAboutData] = useState({
    student_count: 0,
    teacher_count: 0,
    executed_exams: 0,
    blog_count: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAboutData = async () => {
      try {
        setLoading(true);
        const response = await ApiServices.get("Home/AboutUs");
        setAboutData(response);
      } catch (err) {
        setError("فشل في تحميل البيانات");
      } finally {
        setLoading(false);
      }
    };
    fetchAboutData();
  }, []);

  const values = [
    {
      icon: faRocket,
      title: "الابتكار الرقمي",
      desc: "نحن في سباق دائم مع الزمن لتقديم أحدث التقنيات التعليمية التي تجعل التعلم أسهل وأكثر فاعلية.",
    },
    {
      icon: faShieldAlt,
      title: "بيئة آمنة",
      desc: "حماية بيانات الطلاب والمعلمين هي أولويتنا القصوى، نوفر بيئة رقمية مشفرة وموثوقة بالكامل.",
    },
    {
      icon: faStar,
      title: "معايير التميز",
      desc: "لا نرضى بأقل من الكمال في جودة المحتوى التعليمي وتجربة المستخدم التي نقدمها.",
    },
    {
      icon: faHeart,
      title: "التمكين التعليمي",
      desc: "رسالتنا هي تمكين كل طالب من الوصول إلى إمكانياته الكاملة من خلال أدوات ذكية ومبسطة.",
    },
    {
      icon: faUsers,
      title: "الشراكة التعليمية",
      desc: "نؤمن بأن النجاح يتحقق من خلال التكامل بين المعلم والطالب وولي الأمر في منصة واحدة.",
    },
    {
      icon: faBook,
      title: "المحتوى المتجدد",
      desc: "نحدث قواعد بياناتنا باستمرار لنواكب أحدث المناهج الدراسية وأساليب الاختبارات العالمية.",
    },
  ];

  if (loading) return <div className="ab-root"><Header /><div className="ab-loader-box"><Loader /></div></div>;

  return (
    <div className="ab-root" dir="rtl">
      <Header />

      {/* Hero Section */}
      <section className="ab-hero">
        <div className="ab-container">
          <motion.div
            className="ab-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
          >
            <motion.div className="ab-eyebrow" variants={fadeUp} custom={0}>
              <span className="ab-eyebrow-dot" />
              قصتنا ورؤيتنا
            </motion.div>
            <motion.h2 className="ab-title" variants={fadeUp} custom={1}>
              نصنع مستقبل <em>التعليم الذكي</em>
            </motion.h2>
            <motion.p className="ab-subtitle" variants={fadeUp} custom={2}>
              منصة امتحاني هي رحلة طموحة لتحويل التحديات التعليمية إلى فرص نجاح حقيقية، نجمع بين شغف التعليم وقوة التكنولوجيا.
            </motion.p>
          </motion.div>

          <motion.div
            className="ab-hero-visual"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="ab-visual-inner">
              <img src="/assets/images/new_log_design.svg" alt="EMt7any" />
              <div className="ab-glow-bg" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="ab-section">
        <div className="ab-container">
          <motion.div
            className="ab-header"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div className="ab-eyebrow" variants={fadeUp} custom={0}>
              قيمنا الأساسية
            </motion.div>
            <motion.h2 className="ab-title" variants={fadeUp} custom={1}>
              مبادئ تقودنا <em>نحو النجاح</em>
            </motion.h2>
          </motion.div>

          <motion.div
            className="ab-values-grid"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {values.map((v, i) => (
              <ValueCard key={i} {...v} index={i} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Founder Section */}
      <section className="ab-section ab-founder-section">
        <div className="ab-container">
          <motion.div
            className="ab-founder-card"
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <div className="ab-founder-img">
              <img src={ownerImg} alt="Mahmoud Ibrahim" />
              <div className="ab-founder-badge">المؤسس</div>
            </div>

            <div className="ab-founder-content">
              <FontAwesomeIcon icon={faQuoteRight} className="ab-quote" />
              <h2 className="ab-founder-title">رسالة من <em>القلب</em></h2>
              <div className="ab-founder-text">
                <p>
                  "في امتحاني، لا نبني مجرد منصة، بل نبني جسوراً نحو المستقبل. إيماننا الراسخ بأن التعليم حق لكل طالب يدفعنا لابتكار أدوات تجعل التعلم رحلة ممتعة وفعالة."
                </p>
                <p>
                  "هدفنا هو تمكين المعلم، وإلهام الطالب، وطمأنة ولي الأمر. نحن هنا لنصنع الفرق في كل بيت مصري وعربي."
                </p>
              </div>
              <div className="ab-founder-footer">
                <h4>Mahmoud Ibrahim Mohamed</h4>
                <span>Founder & CEO</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />

      <style jsx>{`
        .ab-root {
          font-family: "Zain", sans-serif;
          background: #fff;
          min-height: 100vh;
          direction: rtl;
          overflow-x: hidden;
        }

        .ab-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .ab-section {
          padding: 100px 0;
        }

        /* ── Header ── */
        .ab-header { text-align: center; margin-bottom: 72px; }
        .ab-eyebrow {
          display: inline-flex; align-items: center; gap: 10px;
          color: #6366f1; font-weight: 800; font-size: 0.85rem;
          letter-spacing: 0.14em; text-transform: uppercase;
          margin-bottom: 24px;
        }
        .ab-eyebrow-dot {
          width: 8px; height: 8px; border-radius: 50%; background: #6366f1;
          animation: ab-pulse 2.2s ease-in-out infinite;
        }
        @keyframes ab-pulse {
          0%,100%{ box-shadow: 0 0 0 0 rgba(232,146,10,0.5); }
          50%{ box-shadow: 0 0 0 8px rgba(232,146,10,0); }
        }
        .ab-title {
          font-size: clamp(2.4rem, 4.5vw, 4rem);
          font-weight: 900; color: #0f172a; margin: 0 0 20px;
          line-height: 1.18; letter-spacing: -0.025em;
        }
        .ab-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #6366f1 60%, #6366f1 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
          background-clip: text;
        }
        .ab-subtitle {
          font-size: 1.2rem; color: #64748b; line-height: 1.85; margin: 0 auto;
          max-width: 700px; font-weight: 500;
        }

        /* ── Hero ── */
        .ab-hero { padding: 160px 0 100px; background: #fcfcfd; border-bottom: 1px solid #f1f5f9; }
        .ab-hero-visual { display: flex; justify-content: center; margin-top: 60px; }
        .ab-visual-inner { position: relative; width: 280px; }
        .ab-visual-inner img { width: 100%; position: relative; z-index: 2; filter: drop-shadow(0 20px 40px rgba(0,0,0,0.05)); }
        .ab-glow-bg {
          position: absolute; inset: -20%; background: radial-gradient(circle, rgba(99, 102, 241, 0.05) 0%, transparent 70%);
          z-index: 1; border-radius: 50%;
        }

        /* ── Values ── */
        .ab-values-grid {
          display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 24px;
        }
        .ab-value-card {
          padding: 48px 32px; background: #fff; border: 1.5px solid rgba(15,23,42,0.06);
          border-radius: 32px; text-align: center; transition: 0.3s;
        }
        .ab-value-card:hover { border-color: rgba(15,23,42,0.12); box-shadow: 0 20px 40px rgba(0,0,0,0.03); }
        .ab-value-icon-wrap {
          width: 72px; height: 72px; background: #f8fafc; color: #6366f1;
          border-radius: 20px; display: flex; align-items: center; justify-content: center;
          font-size: 1.8rem; margin: 0 auto 28px; transition: 0.3s;
        }
        .ab-value-card:hover .ab-value-icon-wrap { background: #6366f1; color: #fff; }
        .ab-value-title { font-size: 1.5rem; font-weight: 900; color: #0f172a; margin-bottom: 16px; }
        .ab-value-desc { font-size: 1.05rem; color: #64748b; line-height: 1.8; font-weight: 500; }

        /* ── Founder ── */
        .ab-founder-section { background: #fcfcfd; }
        .ab-founder-card {
          display: grid; grid-template-columns: 1.2fr 2fr; gap: 60px;
          background: #fff; padding: 60px; border-radius: 40px;
          border: 1.5px solid rgba(15,23,42,0.06); align-items: center;
        }
        .ab-founder-img { position: relative; }
        .ab-founder-img img { width: 100%; border-radius: 30px; box-shadow: 0 20px 40px rgba(0,0,0,0.1); }
        .ab-founder-badge {
          position: absolute; bottom: 20px; left: 20px; background: #6366f1;
          color: #fff; padding: 6px 18px; border-radius: 50px; font-weight: 800; font-size: 0.9rem;
        }
        .ab-founder-content { position: relative; }
        .ab-quote { font-size: 3.5rem; color: #6366f1; opacity: 0.1; position: absolute; top: -20px; right: -20px; }
        .ab-founder-title { font-size: 2.2rem; font-weight: 900; color: #0f172a; margin-bottom: 24px; }
        .ab-founder-title em {
          font-style: normal;
          background: linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%);
          -webkit-background-clip: text; -webkit-text-fill-color: transparent;
        }
        .ab-founder-text { font-size: 1.15rem; color: #475569; line-height: 1.9; font-weight: 500; }
        .ab-founder-text p { margin-bottom: 16px; }
        .ab-founder-footer { margin-top: 40px; border-top: 1px dashed #e2e8f0; padding-top: 24px; }
        .ab-founder-footer h4 { font-size: 1.4rem; font-weight: 900; color: #0f172a; margin: 0; }
        .ab-founder-footer span { color: #94a3b8; font-weight: 700; font-size: 0.95rem; }

        .ab-loader-box { height: 70vh; display: flex; align-items: center; justify-content: center; }

        @media (max-width: 1024px) {
          .ab-founder-card { grid-template-columns: 1fr; text-align: center; padding: 40px; }
          .ab-founder-img { max-width: 400px; margin: 0 auto; }
          .ab-founder-content { text-align: center; }
          .ab-quote { display: none; }
        }

        @media (max-width: 768px) {
          .ab-hero { padding-top: 140px; }
          .ab-container { padding: 0 20px; }
          .ab-title { font-size: 2.2rem; }
          .ab-founder-card { padding: 30px 20px; }
          .ab-founder-title { font-size: 1.8rem; }
        }
      `}</style>
    </div>
  );
};

export default AboutUs;
